import { checkIsErrorToolOutput } from "@repo/app-types/agents/errorToolOutput";
import type {
  GetPlaybookTeamScoresToolInput,
  GetPlaybookTeamScoresToolOutput,
  PlaybookTeamScoreCriterion,
} from "@repo/app-types/agents/getPlaybookTeamScoresTool";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { IconChartBar } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import {
  formatCriterionScore,
  formatCriterionScoreDelta,
} from "./playbookScoreFormatting.js";
import { SelectedPlaybookSection } from "./selectedPlaybookSection.js";

const RUNNING_LABEL = "Reading team scores across the playbook";

export const GetPlaybookTeamScoresTool: ToolComponent<
  GetPlaybookTeamScoresToolInput,
  GetPlaybookTeamScoresToolOutput
> = (props) => {
  if (props.status.type === "running") {
    return <ChatToolBase status={props.status}>{RUNNING_LABEL}</ChatToolBase>;
  }

  if (checkIsErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while reading team scores
      </ChatToolBase>
    );
  }

  if (props.status.type === "complete" && props.output) {
    const { criteria } = props.output;
    return (
      <ChatToolDisclosure
        status={props.status}
        icon={
          <IconChartBar className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
        }
        label={`Reviewed team scores (${formatCriteriaCount(criteria.length)})`}
      >
        <div className="space-y-3">
          <SelectedPlaybookSection playbook={props.output.playbook} />
          <TeamScores criteria={criteria} />
        </div>
      </ChatToolDisclosure>
    );
  }

  return <ChatToolBase status={props.status}>{RUNNING_LABEL}</ChatToolBase>;
};

const TeamScores: FC<{
  criteria: readonly PlaybookTeamScoreCriterion[];
}> = ({ criteria }) => {
  if (criteria.length === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        No criteria on this playbook.
      </div>
    );
  }

  // The trend columns only appear when the call requested them (i.e. any
  // criterion carries a change), so a current-only snapshot stays compact.
  const hasTrend = criteria.some((criterion) => criterion.change !== null);
  const rowGrid = hasTrend
    ? "grid grid-cols-[minmax(0,1fr)_3.5rem_4rem] items-baseline gap-2"
    : "grid grid-cols-[minmax(0,1fr)_3.5rem] items-baseline gap-2";

  return (
    <div className="space-y-1.5 text-xs text-gray-600">
      <div
        className={twMerge(
          rowGrid,
          "text-[10px] font-semibold tracking-wide text-gray-400 uppercase",
        )}
      >
        <div>Criterion</div>
        <div className="text-right">Score</div>
        {hasTrend ? <div className="text-right">Change</div> : null}
      </div>
      {criteria.map((criterion) => (
        <div key={criterion.criterion_id} className={rowGrid}>
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">
              {criterion.criterion_name}
            </div>
            {criterion.section_path.length > 0 ? (
              <div className="truncate text-muted-foreground">
                {criterion.section_path.join(" / ")}
              </div>
            ) : null}
          </div>
          <div className="text-right text-foreground tabular-nums">
            {formatCriterionScore(
              criterion.score,
              criterion.grading_value_type,
            )}
          </div>
          {hasTrend ? (
            <div className="text-right tabular-nums">
              <ChangeBadge
                change={criterion.change}
                gradingValueType={criterion.grading_value_type}
              />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

const ChangeBadge: FC<{
  change: number | null;
  gradingValueType: PlaybookTeamScoreCriterion["grading_value_type"];
}> = ({ change, gradingValueType }) => {
  if (change === null || change === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const improved = change > 0;
  return (
    <span className={improved ? "text-green-600" : "text-red-600"}>
      {improved ? "↑" : "↓"}
      {formatCriterionScoreDelta(change, gradingValueType)}
    </span>
  );
};

function formatCriteriaCount(count: number): string {
  return count === 1 ? "1 criterion" : `${count} criteria`;
}
